// Core
import React, { useState } from 'react';
import Slider from 'react-slick';
import { Box, Flex, Text, Grid, Image, Button, Divider, useBreakpointValue, useColorMode, useColorModeValue } from '@chakra-ui/react';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Assets
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import { ReactComponent as LinesWhite } from 'assets/svg/lines-white.svg';
import placeholderSpecs from 'assets/img/specs.png';

// Components
import Card from 'components/Card/Card';
import { ReviewsIcon, LeftIcon, RightIcon } from 'components/Icons/Icons';

const ItemCard = ({item, isDark}) => {
	const cardTopLineDark = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 0%, #38A9E1 56.18%, #FFF 68.16%, #38A9E1 82.69%, rgba(56, 169, 225, 0.00) 100%)';
	const cardTopLine = useColorModeValue('unset', cardTopLineDark);

	const lineBg = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 0%, #38A9E1 56.18%, #FFF 68.16%, #38A9E1 82.69%, rgba(56, 169, 225, 0.00) 100%)';
    const lineBgWhite = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 9.63%, #38A9E1 37.41%, #38A9E1 50%, #38A9E1 60.02%, rgba(56, 169, 225, 0.00) 91.14%)';
    const colorLineBg = useColorModeValue(lineBgWhite, lineBg);

	return (
		<Card padding="0px" mb="70px" overflow="hidden">
	        <Box position="absolute" w="100%">
	            <Box bg={cardTopLine} transform="rotate(180deg)" h="1px" />
	            { isDark && <Box bg="#099BFF" filter="blur(38px)" h="31px" /> }
	        </Box>

	        <Flex gap="16px" p="16px">
	        	<Box w={{base: '40px', md: '50px', lg: '100px'}} h={{base: '40px', md: '50px', lg: '100px'}} p={{base: '8px', md: '16px'}} borderRadius="13px" bg="rgba(9, 155, 255, 0.10)">
	        		<Image src={item?.icon || placeholderSpecs} alt="Specs" w="100%" h="100%" objectFit="contain" />
	        	</Box>
		        
			    <Box flex="1" display="flex" flexDirection="column" justifyContent="space-between">
				    <Text zIndex="5" variant="header" fontSize={{base: '18px', md: '20px', lg: '24px'}} fontStyle="normal" whiteSpace="normal" maxW="100%">
				    	{item?.title}
				    </Text>

				    <Box zIndex="2" position="relative" w="100%" m="7px 0 8px 0">
				        <Box bg={colorLineBg} transform="rotate(180deg)" h="2px" />
				        { isDark && <Box bg="#099BFF" filter="blur(38px)" w="100%" h="16px" position="absolute" /> }
				      
					    <Text zIndex="3" position="relative" mt="6px" whiteSpace="break-spaces">{item?.description}</Text>
				   	</Box>
			    </Box>
	        </Flex>

	        { isDark ? 
		        (<Lines className="abstract-lines" style={{ zIndex: 1}} />)
		        :
		        (<LinesWhite className="abstract-lines" style={{ zIndex: 1, opacity: 0.3}} />)
	    	}
	    </Card>
    );
};

const ReviewsSlider = ({ items }) => {
	const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

    const [currentSlide, setCurrentSlide] = useState(0);
    const settings = {
        dots: false, 
        infinite: true,
        speed: 500,
        slidesToShow: 3, 
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 3000,
        centerMode: true, 
        centerPadding: '0%',
        nextArrow: <></>,
    	prevArrow: <></>,
        beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
    };

    const responsiveSettings = useBreakpointValue({
        base: {...settings, slidesToShow: 1}, 
        md: settings 
    });

    const isMobile = useBreakpointValue({ base: true, md: false });

    return (
    	<Box mb="-40px" overflow="visible">
	    	{ items.length > 1 ? (
		    	<Box position="relative" overflow="visible !important"
		    		className="slides-giveaway-specs"
			    	_before={{
						content: '""',
						position: 'absolute',
						background: `linear-gradient(270deg, rgba(5, 10, 25, 0.00) 0%, ${ isDark ? '#050A19' : '#e0eaff'} 100%)`,
						top: '0px',
						height: '100%',
						left: '0px',
						width: '22%',
						zIndex: '9',
						display: isMobile ? 'none' : 'block',
					}}
					_after={{
						content: '""',
						position: 'absolute',
						background: `linear-gradient(90deg, rgba(5, 10, 25, 0.00) 0%, ${ isDark ? '#050A19' : '#e0eaff'} 100%)`,
						top: '0px',
						height: '100%',
						right: '0px',
						width: '22%',
						display: isMobile ? 'none' : 'block',
						zIndex: '9'
					}}>
				        <Box 
				          position="relative"
				          zIndex="1"
				          mt={{base: '16px', md: '32px'}}
				          pt="28px"
				          left="50%"
				          transform="translateX(-50%)"
				          width={{base: '100%', sm: '90vw', md: '120vw'}}
				          overflow={{base: 'visible', md: 'hidden'}}
				          whiteSpace="nowrap">
					            <Slider {...responsiveSettings}>
					                {[...items, ...items].map((item, index) => (
					                    <Box
					                        key={`${item?.title}_$(index}`}
					                        borderRadius="16px"
					                        transform={index === currentSlide ? 'scale(1.2)' : 'scale(1)'}
					                        transition="transform 0.3s ease"
					                        px="38px"
					                        opacity={index === currentSlide ? '1' : '0.64'}
					                    >
					                    	<ItemCard item={item} isDark={isDark} />
					                    </Box>
					                ))}
					            </Slider>
				            
				        </Box>
			        
			    </Box>
		    ) : (
	        	<Box mt="40px">
	        		<ItemCard item={items[0]} isDark={isDark} />
	        	</Box>
	        )}
        </Box>
    );
};

export default ReviewsSlider;
